import React, { useState, useEffect, useMemo, useRef } from 'react'
import { ProjectWithComments } from '../../../../types/project'
import ItemsList from '../ItemsList'
import { MoodboardView } from './MoodboardView'
import { ItemModal, PROJECT_CATEGORIES } from './ItemModal'
import { useProjectItems } from '../../../../hooks/useProjectItems'
import ItemCommentsModal from '../components/ItemCommentsModal'
import ItemImageModal from '../components/ItemImageModal'
import ProjectStagesView from './ProjectStagesView'

import { useRequests } from '../../../../requests/useRequests'
import { AttachmentsSection } from './AttachmentsSection'
import { isMinimumPackage } from '../../../../utils/package-validator'
import { Package } from '../../../../enums/package.enum'
import { useSelector } from 'react-redux'
import { State } from '../../../../types/state'

interface ProjectItemsSectionProps {
  project: ProjectWithComments
  onProjectUpdate: (updatedProject: ProjectWithComments) => void
  onOpenImageModal: (itemId: string, imageUrl?: string) => void
}

const statusOptions = [
  {
    group: 'Decyzje klienta',
    options: [
      { value: 'approved', label: 'Zaakceptowany' },
      { value: 'rejected', label: 'Odrzucony' },
      { value: 'pending', label: 'Oczekujące na decyzję' }
    ]
  },
  {
    group: 'Twoje statusy',
    options: [
      { value: 'none', label: 'Bez statusu' },
      { value: 'in_valuation', label: 'W wycenie' },
      { value: 'ordered', label: 'Zamówione' }
    ]
  }
]

function useOnClickOutside (ref: React.RefObject<any>, handler: () => void) {
  useEffect(() => {
    const listener = (event: MouseEvent) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return
      }
      handler()
    }
    document.addEventListener('mousedown', listener)
    return () => {
      document.removeEventListener('mousedown', listener)
    }
  }, [ref, handler])
}

const ProjectItemsSection: React.FC<ProjectItemsSectionProps> = ({
  project,
  onProjectUpdate,
  onOpenImageModal
}) => {
  const { package: userPackage, packageExpiredAt } = useSelector((state: State) => state.user)
  const requestMaker = useRequests()
  const [viewMode, setViewMode] = useState<'list' | 'table' | 'moodboard'>('list')
  const [showCommentsModal, setShowCommentsModal] = useState(false)
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [selectedItemName, setSelectedItemName] = useState<string>('')
  const [showImageModal, setShowImageModal] = useState(false)
  const [selectedItemForImage, setSelectedItemForImage] = useState<{ id: string, imageUrl?: string } | null>(null)
  const [globalStatusFilter, setGlobalStatusFilter] = useState<string[]>([])
  const [globalCategoryFilter, setGlobalCategoryFilter] = useState('')
  const [globalStageFilter, setGlobalStageFilter] = useState('')
  const allCategories = Array.from(new Set(project.items.map(item => item.category).filter(Boolean)))
  const [searchText, setSearchText] = useState('')

  const filterItemsBySearch = (items: any[]) => {
    if (!searchText.trim()) return items

    const searchLower = searchText.toLowerCase()
    return items.filter(item =>
      item.name?.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower) ||
      item.supplier?.toLowerCase().includes(searchLower) ||
      item.catalogNumber?.toLowerCase().includes(searchLower)
    )
  }

  const {
    itemModalMode,
    itemModalData,
    setItemModalData,
    openAddItemModal,
    openEditModal,
    closeItemModal,
    handleSaveItem,
    handleDeleteItem,
    handleApproveItem,
    handleRejectItem,
    handleChangeInternalItemStatus,
    handleCopyItem,
    handleMoveItemUp,
    handleMoveItemDown
  } = useProjectItems(project, onProjectUpdate)

  // Filtrowanie elementów na podstawie wyszukiwania i innych filtrów
  const filteredItems = useMemo(() => {
    let items = [...project.items]

    // Filtrowanie po wyszukiwaniu
    if (searchText) {
      items = filterItemsBySearch(items)
    }

    // Filtrowanie po kategorii
    if (globalCategoryFilter) {
      items = items.filter(item => item.category === globalCategoryFilter)
    }

    // Filtrowanie po statusie (multiselect, rozdzielone na status i internalStatus)
    if (globalStatusFilter.length > 0) {
      // Podział na statusy klienta i Twoje statusy
      const clientStatuses = ['approved', 'rejected', 'pending']
      const internalStatuses = ['none', 'in_valuation', 'ordered']
      const selectedClientStatuses = clientStatuses.filter(cs => globalStatusFilter.includes(cs))
      const selectedInternalStatuses = internalStatuses.filter(is => globalStatusFilter.includes(is))
      items = items.filter(item => {
        const matchClient = selectedClientStatuses.length === 0 || selectedClientStatuses.some(cs => item.status === cs)
        const matchInternal = selectedInternalStatuses.length === 0 || selectedInternalStatuses.some(is => item.internalStatus === is)
        return matchClient && matchInternal
      })
    }

    // Filtrowanie po sekcji
    if (globalStageFilter) {
      items = items.filter(item => {
        const stage = project.stages.find(s => s.name === globalStageFilter)
        return (stage != null) && item.stageId === stage.id
      })
    }

    return items
  }, [project.items, searchText, globalCategoryFilter, globalStatusFilter, globalStageFilter, filterItemsBySearch, project.stages])

  // Funkcja licząca sumę wartości elementów wyceny z uwzględnieniem filtrów
  const totalValue = useMemo(() => {
    return filteredItems
      .reduce((total, item) => {
        const basePrice = item.price * item.quantity
        const withDelivery = basePrice + (item.deliveryCost || 0)
        const withMargin = withDelivery * (1 + ((item.marginPercentage || 0) / 100))
        const withVat = withMargin * (1 + ((item.vatPercentage || 0) / 100))
        const withDiscount = withVat * (1 - ((item.discountPercentage || 0) / 100))
        return total + withDiscount
      }, 0)
  }, [filteredItems])

  const handleCloseComments = () => {
    setShowCommentsModal(false)
    setSelectedItemId(null)
    setSelectedItemName('')
  }

  const handleCloseImageModal = () => {
    setShowImageModal(false)
    setSelectedItemForImage(null)
  }

  const handleImageSave = async () => {
    handleCloseImageModal()
    const updatedProject = await requestMaker.getProject(project.id)
    if (updatedProject) {
      onProjectUpdate(updatedProject.data as ProjectWithComments)
    }
  }

  const handleToggleVisibility = async (itemId: string, isHidden: boolean) => {
    try {
      await requestMaker.updateProjectItemVisibility(project.id, itemId, isHidden)
      const response = await requestMaker.getProject(project.id)
      if (response.data) {
        onProjectUpdate(response.data as ProjectWithComments)
      }
    } catch (error) {
      console.error('Błąd podczas aktualizacji widoczności elementu', error)
    }
  }

  const selectBaseClasses = 'tw-appearance-none tw-block tw-w-full sm:tw-w-auto tw-bg-white tw-border tw-border-gray-300 tw-rounded-md tw-text-sm tw-text-gray-700 tw-shadow-sm focus:tw-outline-none focus:tw-border-indigo-500 tw-py-1.5 tw-px-3'

  return (
    <div className='row mb-30'>
      <div className='col-sm-12'>
        <div className='card p-30'>
          <div className='projectCard'>
            <div className='tw-flex tw-flex-col lg:tw-flex-row lg:tw-justify-between lg:tw-items-center tw-mb-5 tw-gap-4'>
              {/* Kontener na filtry */}
              <div className='project-filters tw-flex tw-flex-wrap tw-gap-3'>
                <input
                  type='text'
                  className='form-control form-control-sm tw-w-full sm:tw-w-auto sm:tw-min-w-[200px]'
                  placeholder='Szukaj po nazwie, notatce, dostawcy lub nr katalogowym...'
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <select
                  className='form-control form-control-sm tw-w-full sm:tw-w-auto sm:tw-min-w-[180px]'
                  value={globalStageFilter}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setGlobalStageFilter(e.target.value)}
                >
                  <option value=''>Wszystkie sekcje</option>
                  {project.stages.map(stage => (
                    <option key={stage.id} value={stage.name}>{stage.name}</option>
                  ))}
                </select>
                <select
                  className='form-control form-control-sm tw-w-full sm:tw-w-auto sm:tw-min-w-[180px]'
                  value={globalCategoryFilter}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setGlobalCategoryFilter(e.target.value)}
                >
                  <option value=''>Wszystkie kategorie</option>
                  {allCategories.map(cat => {
                    const found = PROJECT_CATEGORIES.find(c => c.id === cat)
                    return (
                      <option key={cat} value={cat}>{(found != null) ? found.label : cat}</option>
                    )
                  })}
                </select>
                {isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && (
                  <DropdownMultiselectStatus
                    selected={globalStatusFilter}
                    setSelected={setGlobalStatusFilter}
                  />
                )}
                <select
                  className='form-control form-control-sm tw-w-full sm:tw-w-auto sm:tw-min-w-[140px]' // Używamy Tailwind dla min-width
                  value={viewMode}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setViewMode(e.target.value as 'list' | 'table' | 'moodboard')}
                    // styl inline minWidth: 140 został zastąpiony klasą Tailwind sm:tw-min-w-[140px]
                >
                  <option value='list'>Lista</option>
                  <option value='table'>Tabela</option>
                  <option value='moodboard'>Moodboard</option>
                </select>
              </div>

            </div>

            <div className='projectCard__content'>
              {viewMode === 'table' && (
                <ItemsList
                  project={{ ...project, items: filteredItems }}
                  openEditModal={openEditModal}
                  handleDeleteItem={handleDeleteItem}
                  handleApproveItem={handleApproveItem}
                  handleRejectItem={handleRejectItem}
                  handleCopyItem={handleCopyItem}
                  handleMoveItemUp={handleMoveItemUp}
                  handleMoveItemDown={handleMoveItemDown}
                  showAllColumns
                  selectedColumns={project.selectedColumns|| {}}
                  globalCategoryFilter={globalCategoryFilter}
                  globalStatusFilter={globalStatusFilter}
                  globalStageFilter={globalStageFilter}
                  onProjectUpdate={onProjectUpdate}
                />
              )}

              {viewMode === 'moodboard' && (
                <MoodboardView
                  project={{ ...project, items: filteredItems }}
                  openEditModal={openEditModal}
                  handleApproveItem={handleApproveItem}
                  handleRejectItem={handleRejectItem}
                  handleCopyItem={handleCopyItem}
                  handleDeleteItem={handleDeleteItem}
                  globalCategoryFilter={globalCategoryFilter}
                  globalStatusFilter={globalStatusFilter}
                  globalStageFilter={globalStageFilter}
                  onOpenImageModal={onOpenImageModal}
                />
              )}

              {viewMode === 'list' && (
                <ProjectStagesView
                  project={{ ...project, items: filteredItems }}
                  onProjectUpdate={onProjectUpdate}
                  itemModalMode={itemModalMode}
                  itemModalData={itemModalData}
                  setItemModalData={setItemModalData}
                  closeItemModal={closeItemModal}
                  handleSaveItem={handleSaveItem}
                  handleApproveItem={handleApproveItem}
                  handleRejectItem={handleRejectItem}
                  handleChangeInternalItemStatus={handleChangeInternalItemStatus}
                  openEditModal={openEditModal}
                  globalCategoryFilter={globalCategoryFilter}
                  globalStatusFilter={globalStatusFilter}
                  onOpenImageModal={onOpenImageModal}
                  openAddItemModalForStage={openAddItemModal}
                  globalStageFilter={globalStageFilter}
                />
              )}
              {/* Kontener na sumę */}
              <div
                className='project-items-total-value tw-min-w-[160px] tw-text-right lg:tw-mt-0 tw-w-full lg:tw-w-auto tw-mt-3'
                style={{ fontWeight: 600, fontSize: 17, color: '#388a36', marginBottom: 30 }}
              >
                Suma: {totalValue.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PLN
              </div>
              {isMinimumPackage(userPackage, Package.PREMIUM, packageExpiredAt) && <AttachmentsSection projectId={project.id} isList />}
            </div>

          </div>
        </div>
      </div>
      {itemModalMode && (itemModalData != null) && (
        <ItemModal
          isOpen
          onClose={closeItemModal}
          onSave={handleSaveItem}
          mode={itemModalMode}
          itemData={itemModalData}
          setItemData={setItemModalData}
          project={project}
        />
      )}
      {showCommentsModal && selectedItemId && (
        <ItemCommentsModal
          show={showCommentsModal}
          onClose={handleCloseComments}
          projectId={project.id}
          itemId={selectedItemId}
          itemName={selectedItemName}
        />
      )}
      {(selectedItemForImage != null) && (
        <ItemImageModal
          show={showImageModal}
          onClose={handleCloseImageModal}
          onSave={handleImageSave}
          itemId={selectedItemForImage.id}
          projectId={project.id}
          currentImageUrl={selectedItemForImage.imageUrl}
        />
      )}
    </div>
  )
}

const DropdownMultiselectStatus: React.FC<{
  selected: string[]
  setSelected: (val: string[]) => void
}> = ({ selected, setSelected }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useOnClickOutside(ref, () => setOpen(false))

  const allSelected = selected.length === 0
  const selectedLabels = statusOptions.flatMap(g => g.options.filter(opt => selected.includes(opt.value)).map(opt => opt.label))
  const display = allSelected ? 'Wszystkie statusy' : (selectedLabels.length <= 2 ? selectedLabels.join(', ') : `${selectedLabels.length} wybrane`)

  const toggle = (val: string) => {
    if (selected.includes(val)) {
      setSelected(selected.filter((s) => s !== val))
    } else {
      setSelected([...selected, val])
    }
  }

  return (
    <div ref={ref} className='form-control form-control-sm tw-w-full sm:tw-w-auto sm:tw-min-w-[180px] tw-relative' style={{ cursor: 'pointer', background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, minHeight: 36, display: 'flex', alignItems: 'center', padding: '0 12px' }}>
      <div onClick={() => setOpen(o => !o)} style={{ flex: 1, userSelect: 'none', color: allSelected ? '#888' : '#222' }}>
        {display}
      </div>
      <span style={{ marginLeft: 8, color: '#888' }}>
        <svg width='18' height='18' viewBox='0 0 20 20' fill='none'><path d='M6 8L10 12L14 8' stroke='#888' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' /></svg>
      </span>
      {open && (
        <div className='tw-absolute tw-z-50 tw-bg-white tw-shadow-lg tw-rounded-md tw-mt-2 tw-p-2' style={{ minWidth: 220, left: 0, top: '110%' }}>
          {statusOptions.map(group => (
            <div key={group.group} className='tw-mb-2'>
              <div className='tw-text-xs tw-font-semibold tw-text-gray-500 tw-mb-1'>{group.group}</div>
              {group.options.map(opt => (
                <label key={opt.value} className='tw-flex tw-items-center tw-gap-2 tw-text-sm tw-cursor-pointer tw-py-1'>
                  <input
                    type='checkbox'
                    checked={selected.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ProjectItemsSection
